import React, { useEffect, useState } from "react";
import { useEvent } from "../../contexts/EventContext";
import { useUser } from "../../contexts/AuthContext";
import { OtpVerification } from "kh-design-system/components";
import { Auth } from "aws-amplify";
import { authentication, authenticationModuleNames, errors } from "../../utils/constants";
import { setJwtToken } from "../../crud/axios";

const OtpVerificationWrapper = ({
	isSignIn,
	isSignUp,
	setSwitchComponent,
	setEmailValue,
	requestOtpEmail,
	isError,
	setIsError,
	toasterMessage,
	setToasterMessage,
	notificationPopup,
	setNotificationPopup,
	...props
}: {
	isSignIn: boolean;
	isSignUp: boolean;
	setSwitchComponent: React.Dispatch<React.SetStateAction<string>>;
	setEmailValue: React.Dispatch<React.SetStateAction<string>>;
	requestOtpEmail: string;
	isError: boolean;
	setIsError: React.Dispatch<React.SetStateAction<boolean>>;
	toasterMessage: string;
	setToasterMessage: React.Dispatch<React.SetStateAction<string>>;
	notificationPopup: boolean;
	setNotificationPopup: React.Dispatch<React.SetStateAction<boolean>>;
	[key: string]: any;
}) => {
	const { eventDetails, setShowPageLoader } = useEvent();
	const primaryColor = eventDetails?.gallery_brand_color;
	const fontFamily = eventDetails?.gallery_font;
	const { setUser } = useUser();

	const [loading, setLoading] = useState(false);

	const [otp, setOtp] = useState(Array(6).fill(""));
	const [timer, setTimer] = useState(30);
	const [showTimer, setShowTimer] = useState(true);
	const [verificationError, setVerificationError] = useState("");
	const [currentUser, setCurrentUser] = useState(null);

	const handleResendSignInOtp = async (requestOtpEmail: string) => {
		setOtp(Array(6).fill(""));
		setTimer(30);
		setShowTimer(true);
		try {
			if (requestOtpEmail) {
				const response = await Auth.signIn(requestOtpEmail || "");
				if (response) {
					setToasterMessage(authentication.success.OTP_SENT_SUCCESS);
					setNotificationPopup(true);
				}
			} else {
				setVerificationError(authentication.error.INVALID_EMAIL);
			}
		} catch (error) {
			console.error("Error during OTP resend:", error);
			setIsError(true);
			setToasterMessage(authentication.error.FAILED_RESENT_OTP);
		}
		setVerificationError("");
	};

	const handleResendSignUpOtp = async (requestOtpEmail: string) => {
		setOtp(Array(6).fill(""));
		setTimer(30);
		setShowTimer(true);
		try {
			if (requestOtpEmail) {
				const response = await await Auth.resendSignUp(
					requestOtpEmail?.toLowerCase() || ""
				);
				if (response) {
					setToasterMessage(authentication.success.OTP_SENT_SUCCESS);
					setNotificationPopup(true);
				}
			} else {
				setVerificationError(authentication.error.INVALID_EMAIL);
			}
		} catch (error) {
			console.error("Error during OTP resend:", error);
			setIsError(true);
			setToasterMessage(authentication.error.FAILED_RESENT_OTP);
		}
		setVerificationError("");
	};

	const handleVerifyOtpLogin = async (requestOtpEmail: string) => {
		if (!requestOtpEmail) {
			setIsError(true);
			setToasterMessage(authentication.error.INVALID_EMAIL);
			setNotificationPopup(true);
			setTimeout(() => {
				setSwitchComponent(authenticationModuleNames.SIGN_IN);
			}, 700);
			return;
		}
		setLoading(true);
		try {
			const confirmedUser = await Auth.sendCustomChallengeAnswer(currentUser, otp.join(""));
			const sessionResponse = await Auth.currentSession();
			if (confirmedUser && sessionResponse) {
				const clickedActions = [
					{ clickedKey: "isFaceSearchClicked", openKey: "openFaceSearch" },
					{ clickedKey: "isDownloadClicked", openKey: "openDownloadPopup" }
				];

				clickedActions.forEach(({ clickedKey, openKey }) => {
					if (localStorage.getItem(clickedKey)) {
						localStorage.setItem(openKey, "true");
						localStorage.removeItem(clickedKey);
					}
				});
				setToasterMessage(authentication.success.ACCOUNT_VERIFIED_SUCCESS);
				setNotificationPopup(true);
				setUser((user: any) => ({
					...user,
					session: sessionResponse,
					user: confirmedUser
				}));
				setJwtToken(sessionResponse?.getIdToken()?.getJwtToken());
				setLoading(false);
				setShowPageLoader(false);
			}
		} catch (err: any) {
			setIsError(true);
			setToasterMessage(err?.message || errors.SOMETHING_WENT_WRONG);
			setNotificationPopup(true);
			setLoading(false);
			console.error("Error confirming OTP:", err);
		}
	};

	const handleVerifyOtpSignUp = async (requestOtpEmail: string) => {
		if (!requestOtpEmail) {
			setIsError(true);
			setToasterMessage(authentication.error.INVALID_EMAIL);
			setNotificationPopup(true);
			setTimeout(() => {
				setSwitchComponent(authenticationModuleNames.SIGN_UP);
			}, 700);
			return;
		}
		setLoading(true);

		try {
			const cognitoUser = await Auth.confirmSignUp(
				requestOtpEmail.toLowerCase(),
				otp.join("")
			);
			if (cognitoUser) {
				setToasterMessage(authentication.success.ACCOUNT_VERIFIED_SUCCESS);
				setNotificationPopup(true);
				if (sessionStorage.getItem("email") && sessionStorage.getItem("password")) {
					const userDetails = await Auth.signIn(
						sessionStorage.getItem("email") || "",
						sessionStorage.getItem("password") || ""
					);
					sessionStorage.removeItem("email");
					sessionStorage.removeItem("password");
					if (userDetails) {
						const sessionResponse = await Auth.currentSession();
						setJwtToken(sessionResponse?.getIdToken()?.getJwtToken());
						setUser((user: any) => ({
							...user,
							session: sessionResponse,
							user: userDetails
						}));
						setLoading(false);
					}
				} else {
					setEmailValue(requestOtpEmail);
					setLoading(false);
					setTimeout(() => {
						setSwitchComponent(authenticationModuleNames.SIGN_IN);
					}, 700);
				}
			}
		} catch (err: any) {
			setIsError(true);
			setToasterMessage(err?.message || errors.SOMETHING_WENT_WRONG);
			setNotificationPopup(true);
			setLoading(false);
			console.error("Error confirming OTP:", err);
		}
	};

	const userResult = async () => {
		try {
			const userDetails = await Auth.signIn(requestOtpEmail || ""); // No password required for OTP
			if (userDetails) {
				setToasterMessage(authentication.success.CODE_SENT_SUCCESS);
				setNotificationPopup(true);
				setCurrentUser(userDetails);
			}
		} catch (err) {
			console.log(err);
		}
	};

	useEffect(() => {
		if (requestOtpEmail && isSignIn) {
			userResult();
		}
	}, [requestOtpEmail, isSignIn]);

	return (
		<>
			<OtpVerification
				handleResendSignInOtp={handleResendSignInOtp}
				handleResendSignUpOtp={handleResendSignUpOtp}
				handleVerifyOtpLogin={handleVerifyOtpLogin}
				handleVerifyOtpSignUp={handleVerifyOtpSignUp}
				onBackClick={() => {
					if (isSignIn) {
						setSwitchComponent(authenticationModuleNames.SIGN_IN);
					} else if (isSignUp) {
						setSwitchComponent(authenticationModuleNames.SIGN_UP);
					}
				}}
				isLogoVisible={false}
				showTimer={showTimer}
				timer={timer}
				setTimer={setTimer}
				otp={otp}
				setOtp={setOtp}
				setShowTimer={setShowTimer}
				verificationError={verificationError}
				requestOtpEmail={requestOtpEmail}
				isSignUp={isSignUp}
				isSignIn={isSignIn}
				loading={loading}
				primaryColor={primaryColor}
				fontFamily={fontFamily}
			/>
		</>
	);
};

export default OtpVerificationWrapper;
